import { useState, FormEvent } from 'react';

interface FormData {
  name: string;
  phone: string;
  email: string;
  optin: boolean;
  businessName: string;
  businessAddress: string;
  budget: string;
  honeypot: string;
}

export default function MultiStepForm() {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    optin: false,
    businessName: '',
    businessAddress: '',
    budget: '',
    honeypot: ''
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});

  const validateStep1 = () => {
    const newErrors: Partial<FormData> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors: Partial<FormData> = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors: Partial<FormData> = {};
    if (!formData.businessName.trim()) newErrors.businessName = 'Business name is required';
    if (!formData.businessAddress.trim()) newErrors.businessAddress = 'Business address is required';
    if (!formData.budget) newErrors.budget = 'Please select a budget range';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    setErrors({});
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (formData.honeypot) {
      return;
    }

    if (!validateStep3()) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('optin', formData.optin ? 'Yes' : 'No');
      formBody.append('businessName', formData.businessName);
      formBody.append('businessAddress', formData.businessAddress);
      formBody.append('budget', formData.budget);

      const response = await fetch('https://readdy.ai/api/form/d5pjvr2d7uoddce6e180', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          optin: false,
          businessName: '',
          businessAddress: '',
          budget: '',
          honeypot: ''
        });
        setStep(1);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-auto">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Get Your Free Consultation
        </h3>
        <p className="text-sm text-gray-600">
          Step {step} of 3 - Let's discuss your bookkeeping needs
        </p>
      </div>

      <div className="flex items-center justify-between mb-8">
        <div className={`flex-1 h-2 rounded-full ${step >= 1 ? 'bg-teal-500' : 'bg-gray-200'}`}></div>
        <div className={`flex-1 h-2 rounded-full mx-2 ${step >= 2 ? 'bg-teal-500' : 'bg-gray-200'}`}></div>
        <div className={`flex-1 h-2 rounded-full ${step >= 3 ? 'bg-teal-500' : 'bg-gray-200'}`}></div>
      </div>

      <form onSubmit={handleSubmit} data-readdy-form id="bookkeeping-inquiry">
        <input
          type="text"
          name="honeypot"
          value={formData.honeypot}
          onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })}
          style={{ position: 'absolute', left: '-9999px' }}
          tabIndex={-1}
          autoComplete="off"
        />

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="John Smith"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="(915) 555-0123"
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            <button
              type="button"
              onClick={handleNext}
              className="w-full bg-teal-500 text-white py-3 rounded-lg font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Continue
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="john@example.com"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div className="flex items-start">
              <input
                type="checkbox"
                id="optin"
                name="optin"
                checked={formData.optin}
                onChange={(e) => setFormData({ ...formData, optin: e.target.checked })}
                className="mt-1 h-4 w-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
              />
              <label htmlFor="optin" className="ml-2 text-xs text-gray-600 cursor-pointer">
                I agree to receive updates and promotional materials about bookkeeping services
              </label>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleBack}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="flex-1 bg-teal-500 text-white py-3 rounded-lg font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div>
              <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-2">
                Business Name *
              </label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  errors.businessName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="ABC Company LLC"
              />
              {errors.businessName && <p className="text-red-500 text-xs mt-1">{errors.businessName}</p>}
            </div>

            <div>
              <label htmlFor="businessAddress" className="block text-sm font-medium text-gray-700 mb-2">
                Business Address *
              </label>
              <input
                type="text"
                id="businessAddress"
                name="businessAddress"
                value={formData.businessAddress}
                onChange={(e) => setFormData({ ...formData, businessAddress: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  errors.businessAddress ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="123 Main St, El Paso, TX 79901"
              />
              {errors.businessAddress && <p className="text-red-500 text-xs mt-1">{errors.businessAddress}</p>}
            </div>

            <div>
              <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                Monthly Budget Range *
              </label>
              <select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer ${
                  errors.budget ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select your budget</option>
                <option value="Under $99">Under $99</option>
                <option value="$100 - $500">$100 - $500</option>
                <option value="$501 - $1,000">$501 - $1,000</option>
                <option value="$1,001 - $2,000">$1,001 - $2,000</option>
              </select>
              {errors.budget && <p className="text-red-500 text-xs mt-1">{errors.budget}</p>}
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                Thank you! We'll contact you within 24 hours.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                Something went wrong. Please try again.
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleBack}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-teal-500 text-white py-3 rounded-lg font-medium hover:bg-teal-600 transition-colors disabled:bg-gray-400 whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Submitting...' : 'Get Started'}
              </button>
            </div>
          </div>
        )}
      </form>

      <p className="text-xs text-gray-500 text-center mt-4">
        Your information is secure and will never be shared
      </p>
    </div>
  );
}
